import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import FirebaseService from 'services/FirebaseService';
import AuthService from 'services/AuthService';

export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	redirect: '',
	token: localStorage.getItem(AUTH_TOKEN) || null,
	user: null

}

export const signIn = createAsyncThunk('auth/login', async (data, { rejectWithValue }) => {
	const { email, password } = data
	try {
		const response = await AuthService.login({ email, password })
		const token = response.data.token;
		localStorage.setItem(AUTH_TOKEN, token);
		localStorage.setItem('userID', email);
		
		return {
			token,
			user: response.data.user
		};
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const signUp = createAsyncThunk('auth/register', async (data, { rejectWithValue }) => {
	const { email, password } = data
	try {
		const response = await AuthService.register({ email, password })
		const token = response.data.token;
		localStorage.setItem(AUTH_TOKEN, token);
		return token;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const signOut = createAsyncThunk('auth/logout', async () => {
	localStorage.removeItem(AUTH_TOKEN);
	const response = await FirebaseService.signOutRequest()
	return response.data
})

export const signInWithGoogle = createAsyncThunk('auth/signInWithGoogle', async (_, { rejectWithValue }) => {
	try {
		const response = await AuthService.loginInOAuth()
		const token = response.data.token;
		localStorage.setItem(AUTH_TOKEN, token);
		return token;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const signInWithFacebook = createAsyncThunk('auth/signInWithFacebook', async (_, { rejectWithValue }) => {
	try {
		const response = await AuthService.loginInOAuth()
		const token = response.data.token;
		localStorage.setItem(AUTH_TOKEN, token);
		return token;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const signInWithTiktok = createAsyncThunk('auth/signInWithTiktok', async (_, {rejectWithValue }) => {
	try {
		// Step 1: Get the TikTok authorization URL
		const response = await AuthService.initiateTikTokLogin();

		if (response.data.success) {
			// Step 2: Redirect user to TikTok authorization page
			window.location.href = response.data.authUrl;

			// Return a pending state since we're redirecting
			return { redirecting: true };
		} else {
			throw new Error(response.data.error || 'Failed to initiate TikTok login');
		}
	} catch (err) {
		return rejectWithValue(err.response?.data?.error || err.message || 'TikTok login failed')
	}
})

export const initializeAuth = createAsyncThunk('auth/initialize', async (_, { }) => {
	try {
		const token = localStorage.getItem(AUTH_TOKEN);
		if (!token) {
			return { token: null, user: null };
		}

		// Fetch user info using the stored token
		const response = await AuthService.getUserInfo(token);

		if (response.data.success) {
			return {
				token: token,
				user: response.data.user
			};
		} else {
			// Token is invalid, clear it
			localStorage.removeItem(AUTH_TOKEN);
			return { token: null, user: null };
		}
	} catch (err) {
		// Token is invalid or expired, clear it
		localStorage.removeItem(AUTH_TOKEN);
		return { token: null, user: null };
	}
})

export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		authenticated: (state, action) => {
			state.loading = false;
			state.redirect = '/';
			state.token = action.payload.token;
			state.user = action.payload.user;
			state.role = action.payload.user?.role;
			localStorage.setItem(AUTH_TOKEN, action.payload.token);
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload;
			state.showMessage = true;
			state.loading = false;
		},
		hideAuthMessage: (state) => {
			state.message = '';
			state.showMessage = false;
		},
		signOutSuccess: (state) => {
			state.loading = false;
			state.token = null;
			state.user = null;
			localStorage.removeItem(AUTH_TOKEN);
		},
		showLoading: (state) => {
			state.loading = true
		},
		signInSuccess: (state, action) => {
			state.loading = false
			state.token = action.payload
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(signIn.pending, (state) => {
				state.loading = true
			})
			.addCase(signIn.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload.token
				state.user = action.payload.user
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
			})
			.addCase(signOut.rejected, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
			})
			.addCase(signUp.pending, (state) => {
				state.loading = true
			})
			.addCase(signUp.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signUp.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithGoogle.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithGoogle.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signInWithGoogle.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithFacebook.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithFacebook.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signInWithFacebook.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithTiktok.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithTiktok.fulfilled, (state, action) => {
				state.loading = false
				// Don't set redirect here since we're redirecting to TikTok
				if (action.payload.redirecting) {
					state.loading = true; // Keep loading state during redirect
				} else if (action.payload.token && action.payload.user) {
					state.redirect = '/'
					state.token = action.payload.token
					state.user = action.payload.user
				}
			})
			.addCase(signInWithTiktok.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(initializeAuth.pending, (state) => {
				state.loading = true
			})
			.addCase(initializeAuth.fulfilled, (state, action) => {
				state.loading = false
				state.token = action.payload.token
				state.user = action.payload.user
			})
			.addCase(initializeAuth.rejected, (state, action) => {
				state.loading = false
				state.token = null
				state.user = null
			})
	},
})

export const {
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess
} = authSlice.actions

export default authSlice.reducer